export interface ShipStationCredentials {
  /** V1 API key (Basic auth for orders, catalog, order export). */
  apiKey: string;
  apiSecret?: string;
  /** V2 API key (`API-Key` header for inventory and modern V2 endpoints). */
  v2ApiKey?: string;
}

export interface ShipStationConfig {
  shipStationStoreId: string;
  originZip?: string;
  defaultCarrier?: string;
  sandboxMode?: boolean;
  orderExportEnabled?: boolean;
  orderExportIntervalHours?: number;
  /** ISO timestamp — advisory lock to prevent concurrent manual + cron exports. */
  orderExportInProgressUntil?: string;
  lastOrderExportAt?: string;
  lastProductSyncAt?: string;
  lastInventorySyncAt?: string;
  productSyncEnabled?: boolean;
  productSyncIntervalHours?: number;
  inventorySyncEnabled?: boolean;
  inventorySyncIntervalHours?: number;
  sellableOriginFilter?: string[];
}

export const DEFAULT_SHIPSTATION_CONFIG: ShipStationConfig = {
  shipStationStoreId: '',
  sandboxMode: false,
  orderExportEnabled: false,
  orderExportIntervalHours: 6,
  productSyncEnabled: false,
  productSyncIntervalHours: 24,
  inventorySyncEnabled: true,
  inventorySyncIntervalHours: 24,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalBoolean(value: unknown): value is boolean | undefined {
  return value === undefined || typeof value === 'boolean';
}

function isOptionalNumber(value: unknown): value is number | undefined {
  return value === undefined || (typeof value === 'number' && Number.isFinite(value));
}

function isStringArray(value: unknown): value is string[] | undefined {
  if (value === undefined) {
    return true;
  }
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

export function isShipStationCredentials(value: unknown): value is ShipStationCredentials {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.apiKey === 'string' &&
    isOptionalString(value.apiSecret) &&
    isOptionalString(value.v2ApiKey)
  );
}

function isRedactedCredentialValue(value: string | undefined): boolean {
  if (!value) {
    return false;
  }
  return value === '••••••••' || value.startsWith('••');
}

/**
 * Merges a partial credential update with stored values.
 * Blank or redacted fields keep existing secrets.
 */
export function mergeShipStationCredentialsUpdate(
  current: ShipStationCredentials | null,
  candidate: Partial<ShipStationCredentials>
): ShipStationCredentials {
  const apiKey =
    candidate.apiKey === undefined ||
    isRedactedCredentialValue(candidate.apiKey) ||
    candidate.apiKey.trim() === ''
      ? (current?.apiKey ?? '').trim()
      : candidate.apiKey.trim();

  const apiSecret =
    candidate.apiSecret === undefined ||
    isRedactedCredentialValue(candidate.apiSecret) ||
    candidate.apiSecret.trim() === ''
      ? current?.apiSecret
      : candidate.apiSecret.trim() || undefined;

  const v2ApiKey =
    candidate.v2ApiKey === undefined ||
    isRedactedCredentialValue(candidate.v2ApiKey) ||
    candidate.v2ApiKey.trim() === ''
      ? current?.v2ApiKey
      : candidate.v2ApiKey.trim() || undefined;

  return {
    apiKey,
    apiSecret,
    v2ApiKey,
  };
}

export function parseShipStationConfig(value: unknown): ShipStationConfig | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (!isRecord(value)) {
    return undefined;
  }

  if (value.shipStationStoreId !== undefined && typeof value.shipStationStoreId !== 'string') {
    return undefined;
  }

  if (
    !isOptionalString(value.originZip) ||
    !isOptionalString(value.defaultCarrier) ||
    !isOptionalBoolean(value.sandboxMode) ||
    !isOptionalBoolean(value.orderExportEnabled) ||
    !isOptionalNumber(value.orderExportIntervalHours) ||
    !isOptionalString(value.orderExportInProgressUntil) ||
    !isOptionalString(value.lastOrderExportAt) ||
    !isOptionalString(value.lastProductSyncAt) ||
    !isOptionalString(value.lastInventorySyncAt) ||
    !isOptionalBoolean(value.productSyncEnabled) ||
    !isOptionalNumber(value.productSyncIntervalHours) ||
    !isOptionalBoolean(value.inventorySyncEnabled) ||
    !isOptionalNumber(value.inventorySyncIntervalHours) ||
    !isStringArray(value.sellableOriginFilter)
  ) {
    return undefined;
  }

  const config: ShipStationConfig = { ...DEFAULT_SHIPSTATION_CONFIG };

  if (typeof value.shipStationStoreId === 'string') {
    config.shipStationStoreId = value.shipStationStoreId.trim();
  }
  if (typeof value.originZip === 'string') {
    config.originZip = value.originZip;
  }
  if (typeof value.defaultCarrier === 'string') {
    config.defaultCarrier = value.defaultCarrier;
  }
  if (typeof value.sandboxMode === 'boolean') {
    config.sandboxMode = value.sandboxMode;
  }
  if (typeof value.orderExportEnabled === 'boolean') {
    config.orderExportEnabled = value.orderExportEnabled;
  }
  if (typeof value.orderExportIntervalHours === 'number') {
    config.orderExportIntervalHours = value.orderExportIntervalHours;
  }
  if (typeof value.orderExportInProgressUntil === 'string') {
    config.orderExportInProgressUntil = value.orderExportInProgressUntil;
  }
  if (typeof value.lastOrderExportAt === 'string') {
    config.lastOrderExportAt = value.lastOrderExportAt;
  }
  if (typeof value.lastProductSyncAt === 'string') {
    config.lastProductSyncAt = value.lastProductSyncAt;
  }
  if (typeof value.lastInventorySyncAt === 'string') {
    config.lastInventorySyncAt = value.lastInventorySyncAt;
  }
  if (typeof value.productSyncEnabled === 'boolean') {
    config.productSyncEnabled = value.productSyncEnabled;
  }
  if (typeof value.productSyncIntervalHours === 'number') {
    config.productSyncIntervalHours = value.productSyncIntervalHours;
  }
  if (typeof value.inventorySyncEnabled === 'boolean') {
    config.inventorySyncEnabled = value.inventorySyncEnabled;
  }
  if (typeof value.inventorySyncIntervalHours === 'number') {
    config.inventorySyncIntervalHours = value.inventorySyncIntervalHours;
  }
  if (Array.isArray(value.sellableOriginFilter)) {
    config.sellableOriginFilter = value.sellableOriginFilter.map(String);
  }

  return config;
}

export function mergeShipStationConfig(
  current: ShipStationConfig,
  patch: Partial<ShipStationConfig>
): ShipStationConfig {
  const shipStationStoreId =
    patch.shipStationStoreId !== undefined
      ? patch.shipStationStoreId.trim()
      : current.shipStationStoreId;

  return {
    ...current,
    ...patch,
    shipStationStoreId,
  };
}

export function validateShipStationStoreId(
  storeId: string | undefined
): { valid: boolean; message?: string } {
  const trimmed = storeId?.trim() ?? '';
  if (!trimmed) {
    return { valid: false, message: 'ShipStation store ID is required' };
  }
  if (!/^\d+$/.test(trimmed)) {
    return { valid: false, message: 'ShipStation store ID must be a numeric ID' };
  }
  return { valid: true };
}

export interface ValidateShipStationConfigOptions {
  requireStoreId?: boolean;
}

export function validateShipStationConfig(
  config: ShipStationConfig,
  options?: ValidateShipStationConfigOptions
): { valid: boolean; message?: string } {
  if (options?.requireStoreId) {
    return validateShipStationStoreId(config.shipStationStoreId);
  }
  return { valid: true };
}

/** Redacts credential fields for admin GET responses. */
export function redactCredentials(
  credentials: ShipStationCredentials | null | undefined
): ShipStationCredentials | null {
  if (!credentials) {
    return null;
  }
  return {
    apiKey: credentials.apiKey ? '••••••••' : '',
    apiSecret: credentials.apiSecret ? '••••••••' : undefined,
    v2ApiKey: credentials.v2ApiKey ? '••••••••' : undefined,
  };
}

export function validateShipStationCredentials(
  credentials: ShipStationCredentials | null | undefined
): { valid: boolean; message?: string } {
  if (!credentials) {
    return { valid: false, message: 'ShipStation API credentials are required' };
  }
  const apiKey = credentials.apiKey?.trim();
  if (!apiKey) {
    return { valid: false, message: 'ShipStation API key is required' };
  }
  return { valid: true };
}
